import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useSearchParams } from 'react-router-dom';
import CurrencySettings from '../components/CurrencySettings';
import NotificationSettings from '../components/NotificationSettings';
import SensitiveDataSettings from '../components/SensitiveDataSettings';
import PaymentSettings from '../components/PaymentSettings';
import FiscalMonthSettings from '../components/FiscalMonthSettings';
import StaffSettings from '../components/StaffSettings';

// shadcn/ui components
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/ui/tabs';

// Lucide React icons
import { DollarSign, Bell, Shield, QrCode, Calendar, Users } from 'lucide-react';

// Settings tabs configuration
const settingsTabs = [
  {
    id: 'currency',
    label: 'Currency',
    icon: DollarSign,
    component: CurrencySettings,
    translationKey: 'pos.settings.currency.title',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell,
    component: NotificationSettings,
    translationKey: 'pos.settings.notifications.title',
  },
  {
    id: 'sensitive-data',
    label: 'Sensitive Data',
    icon: Shield,
    component: SensitiveDataSettings,
    translationKey: 'pos.settings.sensitiveData.title',
  },
  {
    id: 'payment',
    label: 'Payment',
    icon: QrCode,
    component: PaymentSettings,
    translationKey: 'pos.settings.payment.title',
  },
  {
    id: 'fiscal-month',
    label: 'Fiscal Month',
    icon: Calendar,
    component: FiscalMonthSettings,
    translationKey: 'pos.settings.fiscalMonth.title',
  },
  {
    id: 'staff',
    label: 'Staff Settings',
    icon: Users,
    component: StaffSettings,
    translationKey: 'pos.settings.staff.title',
  },
];

const ModernSettings: React.FC = () => {
  const { translate } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get valid tab IDs from settingsTabs
  const validTabIds = settingsTabs.map((tab) => tab.id);

  // Initialize active tab from URL, localStorage, or default
  const getInitialTab = () => {
    // First check URL parameter
    const urlTab = searchParams.get('tab');
    if (urlTab && validTabIds.includes(urlTab)) {
      return urlTab;
    }
    // Then check localStorage
    const savedTab = localStorage.getItem('settings-active-tab');
    if (savedTab && validTabIds.includes(savedTab)) {
      return savedTab;
    }
    // Default to first tab
    return validTabIds[0] || 'currency';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Sync with URL parameter changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && validTabIds.includes(tab) && tab !== activeTab) {
      setActiveTab(tab);
      localStorage.setItem('settings-active-tab', tab);
    }
  }, [searchParams, validTabIds, activeTab]);

  const handleTabChange = (value: string) => {
    if (!validTabIds.includes(value)) return;

    setActiveTab(value);
    // Save to localStorage for persistence
    localStorage.setItem('settings-active-tab', value);
    // Update URL parameter
    if (value === validTabIds[0]) {
      // If it's the default tab, remove the param
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };

  return (
    <div className="w-full py-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold tracking-tight">
          {translate('pos.settings.title')}
        </h1>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <div className="w-full overflow-x-auto">
          <TabsList className="inline-flex w-max min-w-full gap-1">
            {settingsTabs.map((tab) => {
              const IconComponent = tab.icon;
              const translatedLabel =
                translate(tab.translationKey) || tab.label;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex items-center gap-2 text-xs sm:text-sm"
                >
                  <IconComponent className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden lg:inline">{translatedLabel}</span>
                  <span className="lg:hidden">
                    {translatedLabel.split(' ')[0]}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Tab Content */}
        {settingsTabs.map((tab) => {
          const Component = tab.component;
          return (
            <TabsContent key={tab.id} value={tab.id} className="mt-6">
              <Component />
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

export default ModernSettings;
